import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Icon from 'assets/components/icon/Icon';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import Confirm from 'assets/components/page/templates/Confirm';
import Modal from 'assets/components/page/templates/Modal';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Contract from 'models/sales/contract/Model.Contract';
import useClaimCreator, { ClaimCreation } from 'module/claims/hooks/useClaimCreator';
import { useState } from 'react';
import ClaimSimpleClaimForm from '../simpleClaim/Claim.SimpleClaimForm';
import { ClaimCreatorOptions } from 'models/enums/ClaimCreation';
import { keys } from 'lodash';
import {
  canCreateClaimForContractStatus,
  isClaimContractExpired,
  isClaimContractExpiredWindowExceeded,
} from 'module/claims/helper/Claim.ContractEligibility';

type ClaimCreate = { contract: Contract; isForExpired: boolean; claimCreation: ClaimCreation };
enum ClaimCreationType {
  quick = 'quick',
  simple = 'simple',
}
const ClaimCreateButtons = ({
  contract,
  claimCreatorHook,
}: {
  contract: Contract;
  claimCreatorHook: ReturnType<typeof useClaimCreator>;
}) => {
  const { lang, translate } = getLocales();
  const { isDealer, dealerId } = useAuthStore();
  const {
    canCreateClaim,
    canCreateSimpleClaim,
    isCreatingClaim,
    dealerListApi,
    repairFacilityListApi,
    canCreateClaimForExpiredContract,
    createClaimWithLegacyContract,
  } = claimCreatorHook;

  const expiredContract = isClaimContractExpired(contract);
  const isExpiredWindowExceeded = isClaimContractExpiredWindowExceeded(contract);
  const hasAllowedStatus = canCreateClaimForContractStatus(contract);

  const defaultData: ClaimCreate = {
    contract: undefined,
    isForExpired: expiredContract,
    claimCreation: {
      repairFacilityId: undefined,
      claimCreationOption: undefined,
      dealerId: undefined,
    },
  };

  const [selected, select] = useState<ClaimCreate>(undefined);
  const [simpleClaimData, setSimpleClaimData] = useState(undefined);

  const [showExpiredConfirm, setShowExpiredConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<any>(null);
  const [pendingAction, setPendingAction] = useState<keyof typeof ClaimCreationType>(undefined);

  const handleExpiredCheck = (action: keyof typeof ClaimCreationType, data: any) => {
    if (expiredContract) {
      setPendingAction(action);
      setPendingData(data);
      setShowExpiredConfirm(true);
      return;
    }
    if (action === ClaimCreationType.quick) select(data);
    if (action === ClaimCreationType.simple) setSimpleClaimData(data);
  };

  const proceedAfterConfirm = () => {
    if (pendingAction === ClaimCreationType.quick) {
      select({ ...pendingData });
    }
    if (pendingAction === ClaimCreationType.simple) {
      setSimpleClaimData(contract);
    }
    setShowExpiredConfirm(false);
    setPendingAction(null);
  };

  const isDisabled = (type: keyof typeof ClaimCreationType) => {
    if (isExpiredWindowExceeded) return true;

    const canCreateClaimInternal = hasAllowedStatus && canCreateClaim;
    const canCreateSimpleClaimInternal = hasAllowedStatus && canCreateSimpleClaim;

    if (expiredContract && type === ClaimCreationType.quick)
      return !(canCreateSimpleClaimInternal && canCreateClaimForExpiredContract);
    if (expiredContract && type === ClaimCreationType.simple)
      return !(canCreateSimpleClaimInternal && canCreateClaimForExpiredContract);
    if (type === ClaimCreationType.quick) return !canCreateClaimInternal;
    if (type === ClaimCreationType.simple) return !canCreateSimpleClaimInternal;

    return true;
  };

  return (
    <>
      {/* EXPIRED CONFIRM MODAL */}
      {showExpiredConfirm && (
        <Confirm
          onConfirm={proceedAfterConfirm}
          onClose={() => {
            setShowExpiredConfirm(false);
            setPendingAction(null);
          }}
          actionName={lang.expiredClaimCreationWarning}
          itemName={contract?.displayInfo?.title}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}

      {
        //#region QUICK CLAIM
        <>
          {selected?.contract && isDealer && (
            <Confirm
              data={selected}
              onConfirm={(data) => {
                createClaimWithLegacyContract({
                  contract: data.contract,
                  isForExpired: data.isForExpired,
                  claimCreation: data.claimCreation,
                });
              }}
              onClose={() => select(defaultData)}
              actionName={lang.createClaimForContract}
              itemName={selected?.contract?.displayInfo?.title}
              confirmationType="nextButton"
              subcomponents={ThemeConfirmDefault}
              isLoading={isCreatingClaim}
            />
          )}

          {isDealer ? (
            <Button
              class={ThemeButtonCircle}
              media="fas-car-crash"
              onClick={(data) => {
                handleExpiredCheck(ClaimCreationType.quick, data);
              }}
              isLoading={isCreatingClaim}
              disabled={isCreatingClaim || isDisabled(ClaimCreationType.quick)}
              active
              data={{
                ...defaultData,
                contract,
                claimCreation: {
                  dealerId: dealerId,
                  claimCreationOption: ClaimCreatorOptions.DEALERSHIP,
                },
              }}
              htmlElementProps={{
                title: isExpiredWindowExceeded
                  ? lang.expiredContractClaimWindowExceeded
                  : expiredContract
                    ? lang.createClaimForExpiredContract
                    : lang.createClaimForContract,
              }}
            />
          ) : (
            <>
              <Button
                class={ThemeButtonCircle}
                media="fas-car-crash"
                onClick={(data) => handleExpiredCheck(ClaimCreationType.quick, data)}
                data={{ ...defaultData, contract }}
                isLoading={isCreatingClaim}
                disabled={isCreatingClaim || isDisabled(ClaimCreationType.quick)}
                active
                htmlElementProps={{
                  title: isExpiredWindowExceeded
                    ? lang.expiredContractClaimWindowExceeded
                    : expiredContract
                      ? lang.createClaimForExpiredContract
                      : lang.createClaimForContract,
                }}
              />
              {selected?.contract && (
                <Modal
                  subcomponents={ThemeModalDefault}
                  header={expiredContract ? lang.createClaimForExpiredContract : lang.createClaimForContract}
                  onClose={() => select(defaultData)}
                  render={
                    <FormWrapper>
                      <SelectInput
                        name="claimCreationOption"
                        label={lang.whoIsSubmittingTheClaim}
                        data={keys(ClaimCreatorOptions)
                          .filter((it) => it !== ClaimCreatorOptions.CUSTOMER && it !== ClaimCreatorOptions.OTHER)
                          .map((it) => ({
                            id: it,
                            title: translate(it).replace(/_/g, ' '),
                          }))}
                        value={selected.claimCreation?.claimCreationOption}
                        error={!selected.claimCreation?.claimCreationOption && lang.required}
                        onChange={({ claimCreationOption }) => {
                          select((prev) => ({
                            ...prev,
                            claimCreation: {
                              ...prev.claimCreation,
                              claimCreationOption,
                              dealerId: undefined,
                              repairFacilityId: undefined,
                            },
                          }));
                        }}
                      />
                      {ClaimCreatorOptions.REPAIR_FACILITY === selected.claimCreation?.claimCreationOption && (
                        <SelectInput
                          name="repairFacilityId"
                          label={lang.repairFacility}
                          data={repairFacilityListApi.payload?.data?.map((it) => it.displayInfo)}
                          value={selected.claimCreation?.repairFacilityId}
                          error={
                            ClaimCreatorOptions.REPAIR_FACILITY === selected.claimCreation?.claimCreationOption &&
                            !selected.claimCreation?.repairFacilityId &&
                            lang.required
                          }
                          onChange={({ repairFacilityId }) => {
                            select((prev) => ({
                              ...prev,
                              claimCreation: { ...prev.claimCreation, repairFacilityId },
                            }));
                          }}
                          icon={<Icon class="fas-user" />}
                        />
                      )}
                      {ClaimCreatorOptions.DEALERSHIP === selected.claimCreation?.claimCreationOption && (
                        <SelectInput
                          name="dealerId"
                          label={lang.dealer}
                          data={dealerListApi?.payload?.data?.map((it) => ({
                            ...it.displayInfo,
                            id: it.id,
                          }))}
                          value={selected.claimCreation?.dealerId}
                          error={
                            ClaimCreatorOptions.DEALERSHIP === selected.claimCreation?.claimCreationOption &&
                            !selected.claimCreation?.dealerId &&
                            lang.required
                          }
                          onChange={({ dealerId }) =>
                            select((prev) => ({ ...prev, claimCreation: { ...prev.claimCreation, dealerId } }))
                          }
                          icon={<Icon class="if-cloud" />}
                        />
                      )}
                    </FormWrapper>
                  }
                  footer={
                    <Button
                      onClick={createClaimWithLegacyContract}
                      data={selected}
                      media="fas-save"
                      render={lang.create}
                      class={ThemeButtonCircle}
                      isLoading={isCreatingClaim}
                      disabled={
                        isCreatingClaim ||
                        !(selected.claimCreation?.dealerId || selected.claimCreation?.repairFacilityId)
                      }
                      active
                    />
                  }
                />
              )}
            </>
          )}
        </>
        //#endregion
      }

      {
        //#region SIMPLE CLAIM
        <>
          <Button
            media={<Icon class="fas-car-side" />}
            class={ThemeButtonCircle}
            htmlElementProps={{
              title: isExpiredWindowExceeded
                ? lang.expiredContractClaimWindowExceeded
                : expiredContract
                  ? lang.createClaimForExpiredContract
                  : lang.createSimpleClaim,
            }}
            disabled={isCreatingClaim || isDisabled(ClaimCreationType.simple)}
            active
            onClick={(data) => handleExpiredCheck(ClaimCreationType.simple, data)}
            data={contract}
          />
          {simpleClaimData && (
            <ClaimSimpleClaimForm contract={simpleClaimData} onClose={() => setSimpleClaimData(undefined)} />
          )}
        </>
        //#endregion
      }
    </>
  );
};

export default ClaimCreateButtons;
